import {
  CheckboxCondition,
  CheckboxProgress,
  CheckboxType,
  CONFIG_ELEMENTS,
  type CheckboxItem,
} from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import { useMemo } from 'react'

import { useElementCompleted } from 'components/Editor/EditorElements/hooks/useElementCompleted'
import { getContinueDisable } from 'components/Editor/EditorElements/utils/getContinueDisable'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { continueBlock, showIncompleteElementList } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getBlocksState,
  getBlockState,
  getProject,
  getSection,
} from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { extendTestData } from 'utils/test/qaData'

import type { CheckboxElementType } from '../checkboxTypes'
import CheckboxContainerUlStyled from '../styled/CheckboxContainerStyled'
import RichTextContainer from '../styled/RichTectContainer'
import Checkbox from './Checkbox'

const CheckboxElementPreview: CheckboxElementType = ({
  element,
  mode,
  styles,
  block,
  font,
  state,
  setState,
}) => {
  const { value } = element
  const checkType = value.type
  const isContinueType = checkType === CheckboxType.continue
  const anyCheckedCondition = value.progress === CheckboxProgress.any
  const allCheckedCondition = value.progress === CheckboxProgress.all
  const stateItems = useMemo(() => state?.items || [], [state?.items])
  const blocks = useProjectContext(getBlocksState)
  const section = useProjectContext(getSection)
  const dispatch = useProjectDispatch()
  const isDemoMode = mode.previewMode === PreviewMode.demo
  const showIncompleteModal = useProjectContext(getProject)?.displayIncompleteModal
  const setComplete = useElementCompleted({
    block,
    elementId: element.id,
    completed: false,
    deviceMode: mode.deviceMode,
  })
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.checkbox.type,
      elementId: element.id,
    },
  )

  const condition = value.condition || CheckboxCondition.none
  const disabledBtn = isContinueType
    ? getContinueDisable({ block, condition, blocks, section })
    : false
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result

  const disabled = disabledBtn || Boolean(blockResult)

  const onChange = (value: boolean, item: CheckboxItem) => {
    if ((!value && isContinueType) || isDemoMode) {
      return
    }
    if (disabled && showIncompleteModal) {
      dispatch(showIncompleteElementList())
      return
    }
    const newItems = stateItems.map((i) =>
      i.value === item.value ? { ...i, checked: value, disabled: isContinueType } : i,
    )
    setState?.({ items: newItems })
    const fullChecked = newItems.every(({ checked }) => checked)
    const anyChecked = newItems.some(({ checked }) => checked)

    const shouldComplete =
      (anyCheckedCondition && anyChecked) || (allCheckedCondition && fullChecked)

    if (shouldComplete) {
      if (block && isContinueType) {
        dispatch(continueBlock({ blockId: block.uuid }))
      }
      setComplete(true)
    }
    const data = entityAnalyticsConfig.checked({
      resultExtensions: {
        label: rtValueToText(item.label || ''),
        checked: value,
        itemIndex: stateItems.findIndex(({ value }) => value === item.value),
      },
      objectExtensions: { itemId: item.value, ...baseObjectExtensions },
      elementId: element.id,
    })
    sendXapiData(data)
  }

  return (
    <CheckboxContainerUlStyled $style={styles}>
      {stateItems.map((item, index) => (
        <li key={item.value}>
          <Checkbox
            checked={item.checked}
            disabled={showIncompleteModal ? item.disabled : disabled || item.disabled}
            onChange={(value) => onChange(value, item)}
            styles={styles}
            testData={extendTestData({ index })}
            value={item.value}
          >
            <RichTextContainer $checked={item.checked} $disabled={item.disabled}>
              <RichTextViewer styles={font} value={item?.label || textToRtValue('')} />
            </RichTextContainer>
          </Checkbox>
        </li>
      ))}
    </CheckboxContainerUlStyled>
  )
}

export default CheckboxElementPreview
